import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@auth/AuthProvider';
import { useChurchDataProvider } from '@data/ChurchDataProvider';
import IntegrationsController from '@data/controller/IntegrationsController';
import { StaffAction } from '@interfaces/church.interfaces';
import { ExternalVendor } from '@interfaces/external-vendor.interface';
import {
  PeopleImportJob,
  PeopleImportMode,
  PeopleImportStatus,
  isIntegrationUsable,
  isPeopleImportFinished
} from '@interfaces/integration.interface';

// Quick at first, so the card feels live, then slower: a large import can run
// long enough that a steady 4s would add up to hundreds of requests.
function pollDelay(elapsedMs: number) {
  if (elapsedMs < 60 * 1000) return 4000;
  if (elapsedMs < 5 * 60 * 1000) return 15000;
  return 30000;
}

// The start route's two refusals, from StartPeopleImportForChurch.
const ALREADY_IMPORTED = 'ALREADY_IMPORTED';
const IMPORT_IN_PROGRESS = 'IMPORT_IN_PROGRESS';

/// What the People tab should show about importing, if anything.
export type PeopleImportState =
  | { kind: 'hidden' }
  | { kind: 'available' }
  | { kind: 'inProgress', job: PeopleImportJob }
  | { kind: 'succeeded', job: PeopleImportJob }
  | { kind: 'failed', job: PeopleImportJob };

function newestFirst(jobs: PeopleImportJob[]) {
  return [...jobs].sort((one, two) => two.createdAt.localeCompare(one.createdAt));
}

// Importing is a one-time setup step. Whether it's done comes from the
// connection's permanent peopleImportCompletedAt; the job list is only for
// following an import that's running or reporting one that failed.
export default function usePeopleImport(options: {
  provider?: ExternalVendor;
  onCompleted?: () => void;
} = {}) {
  const provider = options.provider ?? ExternalVendor.PLANNING_CENTER;

  const auth = useAuth();
  const churchID = auth.churchId;
  const { integrations, reloadConnectionStatus } = useChurchDataProvider();

  const connection = integrations?.find((candidate) => candidate.provider === provider) ?? null;
  const isConnected = isIntegrationUsable(connection);
  const importedAt = connection?.peopleImportCompletedAt ?? null;

  // Starting needs an admin; anyone can watch one that's already running.
  const canStart = auth.hasPermission(StaffAction.UPDATE_CONNECTED_SERVICE_CREDENTIALS);

  const [jobs, setJobs] = useState<PeopleImportJob[] | null>(null);
  // The job started or polled in this session, which is newer than the list.
  const [trackedJob, setTrackedJob] = useState<PeopleImportJob | null>(null);
  const [alreadyImported, setAlreadyImported] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  const onCompletedRef = useRef(options.onCompleted);
  onCompletedRef.current = options.onCompleted;

  const fetchJobs = useCallback(async () => {
    if (!churchID) return;

    const response = await IntegrationsController.fetchPeopleImports(churchID, provider, auth);

    if (!response.error && response.body) {
      setJobs(response.body.result ?? []);
    }
  }, [churchID, provider, auth]);

  useEffect(() => {
    if (!isConnected || importedAt) return;

    void fetchJobs();
  }, [isConnected, importedAt, fetchJobs]);

  // Only an apply imports anyone; previews run in the background untouched.
  const latestApply = newestFirst(jobs ?? []).find((job) => job.mode === PeopleImportMode.APPLY) ?? null;
  const activeJob = trackedJob ?? latestApply;

  // An import that finished this session counts before the connection is
  // refetched, so it isn't offered again in between.
  const hasImported = importedAt !== null || alreadyImported ||
    (trackedJob?.mode === PeopleImportMode.APPLY && trackedJob.status === PeopleImportStatus.SUCCEEDED);

  const isRunning = activeJob !== null && !isPeopleImportFinished(activeJob.status);

  // Poll the running job until it finishes, backing off as it runs, and not at
  // all while the tab is hidden.
  useEffect(() => {
    if (!churchID || !activeJob || isPeopleImportFinished(activeJob.status)) return;

    const jobID = activeJob.id;
    const startedPollingAt = Date.now();
    let timeout: number | undefined;
    let isInFlight = false;
    let isCancelled = false;

    const poll = async () => {
      timeout = undefined;
      isInFlight = true;

      const response = await IntegrationsController.fetchPeopleImport(churchID, provider, jobID, auth);
      const job = response.body?.result;

      isInFlight = false;

      if (isCancelled) return;

      if (!response.error && job) {
        setTrackedJob(job);

        if (isPeopleImportFinished(job.status)) {
          if (job.status === PeopleImportStatus.SUCCEEDED) {
            onCompletedRef.current?.();
            // Picks up peopleImportCompletedAt, so every screen agrees it's done.
            void reloadConnectionStatus();
          }

          return;
        }
      }

      schedule();
    };

    const schedule = () => {
      if (isCancelled || document.hidden || timeout !== undefined || isInFlight) return;

      timeout = window.setTimeout(() => {
        void poll();
      }, pollDelay(Date.now() - startedPollingAt));
    };

    // Coming back to the tab checks straight away rather than waiting out a
    // delay that may have been set half an hour ago.
    const onVisibilityChange = () => {
      if (document.hidden) {
        window.clearTimeout(timeout);
        timeout = undefined;
        return;
      }

      // A request already on its way will schedule the next one itself.
      if (timeout === undefined && !isInFlight) {
        void poll();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    schedule();

    return () => {
      isCancelled = true;
      window.clearTimeout(timeout);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
    // The job's identity and whether it's finished are what matter, not every
    // counter update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchID, provider, auth, activeJob?.id, isRunning, reloadConnectionStatus]);

  const start = useCallback(async () => {
    if (!churchID) return;

    setIsStarting(true);
    setError(null);

    try {
      const response = await IntegrationsController.startPeopleImport(
        churchID,
        provider,
        PeopleImportMode.APPLY,
        auth
      );

      const job = response.body?.result;

      if (job && !response.error) {
        setTrackedJob(job);
        return;
      }

      // Neither refusal is really an error to show: one means there's nothing
      // left to do, the other that it's already happening.
      if (response.code === ALREADY_IMPORTED) {
        setAlreadyImported(true);
        void reloadConnectionStatus();
        return;
      }

      if (response.code === IMPORT_IN_PROGRESS) {
        await fetchJobs();
        return;
      }

      setError(response.error ?? 'Unable to start the import right now.');
    } finally {
      setIsStarting(false);
    }
  }, [churchID, provider, auth, fetchJobs, reloadConnectionStatus]);

  let state: PeopleImportState = { kind: 'hidden' };

  if (activeJob && isRunning) {
    state = { kind: 'inProgress', job: activeJob };
  } else if (trackedJob?.status === PeopleImportStatus.SUCCEEDED && !isDismissed) {
    // Finished while this page was open, so it's worth saying so once.
    state = { kind: 'succeeded', job: trackedJob };
  } else if (!isConnected || hasImported || !canStart || jobs === null) {
    state = { kind: 'hidden' };
  } else if (latestApply && isPeopleImportFinished(latestApply.status)) {
    state = { kind: 'failed', job: activeJob ?? latestApply };
  } else {
    state = { kind: 'available' };
  }

  return {
    state,
    providerName: provider === ExternalVendor.PLANNING_CENTER ? 'Planning Center' : 'your provider',
    canStart,
    isStarting,
    error,
    start,
    dismiss: () => setIsDismissed(true)
  } as const;
}
